/**
 * CycleSnapshotSection — Phase 3 "Current Cycle" block for the Metrics tab.
 *
 * TRAINER / ADMIN ONLY. English copy (operational UI language boundary).
 * Powered by the coaching-first snapshot engine
 * (GET /api/cycles/{id}/snapshot — trainer-gated).
 *
 * Answers the 4 curated questions the engine is built around:
 *   1. Did the client actually train?   → adherence tiles
 *   2. Did the client progress?         → PRs in cycle + progression deltas
 *   3. What did we actually train?       → muscle group distribution
 *   4. Where are they in the process?    → week N/M + continuity
 *
 * NO vanity totals, NO averages — mirrors the engine philosophy.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FOCUS_LABELS, formatWeekN, useCurrentCycle } from './useCycles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = { normal: 'Normal', attention: 'Attention', review: 'Review' };
const TIER_DOT = { normal: 'bg-emerald-400', attention: 'bg-amber-400', review: 'bg-purple-400' };

const CADENCE_LABEL = {
  intensive: 'Intensive', steady: 'Steady', light: 'Light',
  irregular: 'Irregular', unknown: 'No data',
};
const ADHERENCE_LABEL = {
  high: 'High adherence', moderate: 'Moderate adherence',
  low: 'Low adherence', unknown: 'No plan on record',
};
const TREND_LABEL = {
  improving: '↗ Improving', stable: '→ Stable',
  regressing: '↘ Regressing', insufficient_data: 'Not enough data',
};
const TREND_COLOR = {
  improving: 'text-emerald-300', stable: 'text-blue-300',
  regressing: 'text-rose-300', insufficient_data: 'text-slate-400',
};

const MUSCLE_BAR_COLORS = [
  'bg-blue-400', 'bg-emerald-400', 'bg-purple-400',
  'bg-amber-400', 'bg-cyan-400', 'bg-pink-400', 'bg-orange-400',
];

const Tile = ({ label, value, sub, testid }) => (
  <div className="bg-slate-900/40 border border-slate-700/50 rounded-md px-3 py-2.5" data-testid={testid}>
    <div className="text-slate-400 text-[11px] uppercase tracking-wider mb-1">{label}</div>
    <div className="text-white text-lg font-semibold leading-tight">{value}</div>
    {sub && <div className="text-slate-400 text-xs mt-0.5">{sub}</div>}
  </div>
);

export const CycleSnapshotSection = ({ clientId }) => {
  const { cycle, loading: cycleLoading } = useCurrentCycle(clientId);
  const [snapshot, setSnapshot] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);

  const fetchSnapshot = useCallback(async (cycleId) => {
    setSnapLoading(true);
    try {
      const r = await axios.get(`${API}/cycles/${cycleId}/snapshot`);
      setSnapshot(r.data);
    } catch (e) {
      console.error('[CycleSnapshotSection] snapshot failed', e);
      setSnapshot(null);
    } finally {
      setSnapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cycle?.id) {
      fetchSnapshot(cycle.id);
    } else {
      setSnapshot(null);
    }
  }, [cycle?.id, fetchSnapshot]);

  // ── Loading ────────────────────────────────────────────────────────────
  if (cycleLoading) {
    return (
      <div className="glass-card p-4" data-testid="cycle-snapshot-loading">
        <div className="text-slate-400 text-sm animate-pulse">Loading current cycle…</div>
      </div>
    );
  }

  // ── Empty: no active cycle ───────────────────────────────────────────────
  if (!cycle) {
    return (
      <div className="glass-card p-4" data-testid="cycle-snapshot-empty">
        <h3 className="text-white font-semibold text-base mb-1">🔄 Current Cycle</h3>
        <p className="text-slate-400 text-sm">
          No active cycle for this client. Create one from the client's profile to track block progress here.
        </p>
      </div>
    );
  }

  const s = snapshot;
  const cont = s?.continuity || {};
  const pattern = s?.training_pattern_summary || {};
  const tier = cont.tier || 'normal';
  const maxMuscleSets = (s?.muscle_group_distribution || []).reduce((m, g) => Math.max(m, g.sets), 0) || 1;

  return (
    <div className="glass-card p-4 space-y-4" data-testid="cycle-snapshot-section">
      {/* Header */}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-white font-semibold text-base">🔄 Current Cycle</h3>
          <div className="text-slate-300 text-sm mt-0.5">
            <span className="font-semibold" data-testid="cycle-snapshot-focus">
              {FOCUS_LABELS[cycle.focus] || cycle.focus}
            </span>
            <span className="text-slate-500 mx-1.5">·</span>
            <span data-testid="cycle-snapshot-week">{formatWeekN(cycle)}</span>
          </div>
          {cycle.focus_note && (
            <div className="text-slate-400 text-xs mt-0.5 italic">{cycle.focus_note}</div>
          )}
        </div>
        {/* Pattern summary chips */}
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800/70 border border-slate-700 text-slate-300">
            {CADENCE_LABEL[pattern.cadence_label] || 'No data'}
          </span>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800/70 border border-slate-700 text-slate-300">
            {ADHERENCE_LABEL[pattern.adherence_label] || 'No plan'}
          </span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full bg-slate-800/70 border border-slate-700 ${TREND_COLOR[pattern.progression_trend] || 'text-slate-400'}`}>
            {TREND_LABEL[pattern.progression_trend] || 'No data'}
          </span>
        </div>
      </div>

      {snapLoading && !s ? (
        <div className="text-slate-400 text-sm animate-pulse">Computing snapshot…</div>
      ) : !s ? (
        <div className="text-slate-400 text-sm">Snapshot unavailable.</div>
      ) : (
        <>
          {/* Q1 + Q4 — adherence & position tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Tile
              label="Adherence"
              value={s.adherence_percent != null ? `${s.adherence_percent}%` : '—'}
              sub={`${s.sessions_completed} / ${s.sessions_planned} sessions`}
              testid="cycle-snapshot-adherence"
            />
            <Tile
              label="Weekly frequency"
              value={s.weekly_frequency_actual != null ? `${s.weekly_frequency_actual}×` : '—'}
              sub="per week (actual)"
              testid="cycle-snapshot-frequency"
            />
            <Tile
              label="Deloads"
              value={s.deloads_in_cycle}
              sub="in this cycle"
              testid="cycle-snapshot-deloads"
            />
            <div className="bg-slate-900/40 border border-slate-700/50 rounded-md px-3 py-2.5" data-testid="cycle-snapshot-continuity">
              <div className="text-slate-400 text-[11px] uppercase tracking-wider mb-1">Continuity</div>
              <div className="text-white text-lg font-semibold leading-tight flex items-center gap-1.5">
                <span className={`inline-block w-2 h-2 rounded-full ${TIER_DOT[tier]}`} />
                {TIER_LABEL[tier]}
              </div>
              <div className="text-slate-400 text-xs mt-0.5">
                {cont.block_weeks_total ?? 0}w accumulated · {cont.active_weeks_in_cycle ?? 0}w active
              </div>
            </div>
          </div>

          {/* Q2 — PRs hit during this cycle */}
          {s.top_prs && s.top_prs.length > 0 && (
            <div data-testid="cycle-snapshot-prs">
              <div className="text-slate-300 text-sm font-medium mb-1.5">🏆 PRs this cycle</div>
              <div className="space-y-1">
                {s.top_prs.map((pr, idx) => (
                  <div
                    key={`${pr.exercise_name}-${idx}`}
                    className="flex items-center justify-between bg-slate-900/40 border border-slate-700/40 rounded px-3 py-1.5 text-sm"
                  >
                    <span className="text-white truncate mr-2">{pr.exercise_name}</span>
                    <span className="text-slate-300 whitespace-nowrap">
                      {pr.reps} × {pr.weight} kg
                      {pr.delta_kg > 0 && (
                        <span className="text-emerald-300 ml-2">+{pr.delta_kg} kg</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Q2 — progression deltas on most-trained lifts */}
          {s.progression_deltas && s.progression_deltas.length > 0 && (
            <div data-testid="cycle-snapshot-progression">
              <div className="text-slate-300 text-sm font-medium mb-1.5">📈 Progression (most-trained lifts)</div>
              <div className="space-y-1">
                {s.progression_deltas.map((d, idx) => {
                  const up = d.delta_kg > 0;
                  const down = d.delta_kg < 0;
                  const color = up ? 'text-emerald-300' : down ? 'text-rose-300' : 'text-blue-300';
                  const arrow = up ? '↗' : down ? '↘' : '→';
                  return (
                    <div
                      key={`${d.exercise_name}-${idx}`}
                      className="flex items-center justify-between bg-slate-900/40 border border-slate-700/40 rounded px-3 py-1.5 text-sm"
                    >
                      <span className="text-white truncate mr-2">
                        {d.exercise_name}
                        <span className="text-slate-500 text-xs ml-1.5">· {d.sessions_in_cycle} ses.</span>
                      </span>
                      <span className="whitespace-nowrap text-slate-300">
                        {d.start_best.weight} → {d.end_best.weight} kg{' '}
                        <span className={`${color} font-medium`}>
                          {arrow} {d.delta_kg > 0 ? '+' : ''}{d.delta_kg} kg ({d.delta_pct > 0 ? '+' : ''}{d.delta_pct}%)
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Q3 — muscle group distribution */}
          {s.muscle_group_distribution && s.muscle_group_distribution.length > 0 && (
            <div data-testid="cycle-snapshot-distribution">
              <div className="text-slate-300 text-sm font-medium mb-1.5">🎯 Working-set distribution</div>
              <div className="space-y-1.5">
                {s.muscle_group_distribution.map((g, idx) => (
                  <div key={g.group}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className={`capitalize ${g.group === 'unmapped' ? 'text-amber-300' : 'text-slate-300'}`}>
                        {g.group === 'unmapped' ? '⚠ Unmapped (clean library)' : g.group}
                      </span>
                      <span className="text-slate-400">{g.sets} sets · {g.pct}%</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${g.group === 'unmapped' ? 'bg-amber-400/70' : MUSCLE_BAR_COLORS[idx % MUSCLE_BAR_COLORS.length]}`}
                        style={{ width: `${(g.sets / maxMuscleSets) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CycleSnapshotSection;
